import {
  ExtranetOrderDto,
  ExtranetOrderListItemDto,
  GetExtranetOrderQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'

import {
  PharmacButton,
  PharmacH6,
  PharmacIcon,
  PharmacSearch,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { OrderHistoryCustomerGroup } from '../../components/order-history/OrderHistoryCustomerGroup'
import { OrderHistoryFilters } from '../../components/order-history/OrderHistoryFilters'
import { useOrderCheckboxes } from '../../components/order-history/useOrderCheckboxes'
import { mapOrderSearchParamsToQueryParams } from '../../components/order-history/util/query-parameter-mapping'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { getPages } from '../../features/infinite-query/util/getPages'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

export type GetOrderHistoryParams = Omit<GetExtranetOrderQueryDto, 'cid' | 'pageSize'>

export const OrderHistoryView = () => {
  const { t } = useLanguage(['extranet', 'common'])

  const { customer } = useCustomerContext()
  const { minDesktop, maxMobile } = useBreakpoint()

  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()

  const checkboxesHook = useOrderCheckboxes()

  const [debouncedParams, setDebouncedParams] = useDebounce({} as GetOrderHistoryParams)

  const [dosageCardButtonEnabled, setDosageCardButtonEnabled] = useState(false)

  const createLink = useExtranetPath()

  const [params, setParams] = useState<GetOrderHistoryParams>(
    mapOrderSearchParamsToQueryParams(searchParams)
  )

  useEffect(() => {
    if (searchInput !== debouncedParams.search) {
      setDebouncedParams((s) => ({ ...s, search: searchInput }))
    }
  }, [searchInput])

  const handleReset = () => {
    setSearchInput('')
    setSearchParams(createSearchParams({}))
  }

  const fetchPage = (pageParam: number) => {
    return api.orders.getOrderHistory(customer!.id!, {
      pageSize: 40,
      ...params,
      page: pageParam,
    })
  }

  const { data, isLoading, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [
      '__orderHistory',
      customer?.id,
      params.sendingDate,
      params.endDate,
      params.page,
      params.search,
      params.startDate,
      params.vnr,
      params.startCreatedDateRange,
      params.endCreatedDateRange,
      params.startSendingDate,
      params.endSendingDate,
      params.hasChanges,
      params.status,
    ],

    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const orders = getPages(data)

  const [filteredOrders, setFilteredOrders] = useState<ExtranetOrderListItemDto[]>([])

  useEffect(() => {
    setFilteredOrders(() => {
      let order = [...getPages(data)]

      if (params.startSendingDate) {
        order = order.filter((a) => a.orders.every((b) => b.status === 'sent'))
      }
      return order
    })
  }, [params, data, isLoading])

  const selectedOrderIds = Object.values(checkboxesHook.checkboxes).flatMap((a) => a)

  const selectedOrders = orders.reduce<ExtranetOrderDto[]>((acc, curr) => {
    const matchingOrders = curr.orders.filter((a) => selectedOrderIds.includes(a.id))

    return acc.concat(matchingOrders)
  }, [])

  const orderHasChangesStatus = selectedOrders.find((a) => a.orderChangesFi.length !== 0)

  useEffect(() => {
    if (selectedOrders.length === 0) return setDosageCardButtonEnabled(false)

    const ordersAreInSentStatus = selectedOrders.every((a) => a.status === 'sent')

    setDosageCardButtonEnabled(ordersAreInSentStatus)
  }, [checkboxesHook.checkboxes])

  const hasActiveFilters = () => {
    const params = mapOrderSearchParamsToQueryParams(searchParams)

    const hasNoSearchParams = Object.keys(params).length === 0

    return !hasNoSearchParams
  }

  const activeFilterCount = () => {
    if (
      Object.keys(params).includes('endCreatedDateRange') &&
      Object.keys(params).includes('endSendingDate')
    )
      return Object.keys(params).length - 2
    if (
      Object.keys(params).includes('endCreatedDateRange') ||
      Object.keys(params).includes('endSendingDate')
    )
      return Object.keys(params).length - 1
    return Object.keys(params).length
  }

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  useEffect(() => {
    setParams(() => mapOrderSearchParamsToQueryParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const getDosingCardData = async () => {
    const ids = selectedOrderIds
    const response = await api.orders.getDosingCardsForOrderIds(
      customer!.id,
      { ids },
      {
        format: 'blob',
      }
    )

    const blob = new Blob([response as unknown as string], {
      type: 'application/pdf',
    })

    window.open(window.URL.createObjectURL(blob), '_blank')
  }

  const getOrderChangesData = async () => {
    if (selectedOrderIds.length > 0 && customer?.id) {
      const baseUrl = createLink('oma-annos.orders.orderChanges', {
        customerId: customer.id,
      })

      const url = selectedOrderIds.reduce<string>((acc, curr) => {
        const isValidOrderId = !!orders.find(
          (a) => !!a.orders.find((b) => b.id === curr && b.orderChangesFi.length > 0)
        )

        if (!isValidOrderId) return acc

        const firstParamExists = /\?o\=/.test(acc)

        return acc + (firstParamExists ? '&o=' : '?o=') + curr
      }, baseUrl)

      return navigate(url)
    }
  }

  const noResults = !isLoading && !isFetchingNextPage && filteredOrders.length === 0

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        flexColumn
        paddingSmallHorizontal={!minDesktop}
        paddingLargeBottom
      >
        <ExtranetContainer full>
          <ExtranetViewHeader title={t('orderHistory.title')}>
            <PharmacSearchWrapper>
              <PharmacSearch
                onChange={(v) => setSearchInput(v)}
                value={searchInput}
                placeholder={t('search', { ns: 'common' })}
                searchButtonContent={
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PharmacIcon
                      icon="search"
                      size="22"
                    />
                  </div>
                }
              />
              <div style={{ width: 'var(--spacing-small)' }} />
              <ExtranetFilters
                activeFilterCount={activeFilterCount()}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={hasActiveFilters()}
                render={({ closeFilters }) => <OrderHistoryFilters closeFilters={closeFilters} />}
              />
            </PharmacSearchWrapper>
          </ExtranetViewHeader>
        </ExtranetContainer>
      </ExtranetContainer>
      <ExtranetContainer
        paddingSmallHorizontal={!minDesktop}
        full
        flexRow
        paddingMediumBottom
      >
        <ExtranetContainer
          full
          marginSmallLeft
        >
          <PharmacH6>{t('orderHistory.orderCount')}</PharmacH6>
          <div style={{ width: 'var(--spacing-small)' }} />
          <PharmacH6>{orders.length}</PharmacH6>
        </ExtranetContainer>

        {minDesktop && (
          <ExtranetContainer
            full
            justifyEnd
          >
            <PharmacTooltip
              position="top-end"
              handle={
                <PharmacIcon
                  icon="info"
                  style={{
                    color: 'var(--color-green)',
                    marginRight: 'var(--spacing-tiny)',
                    marginTop: 'var(--spacing-tiny)',
                  }}
                />
              }
            >
              {t('extranet:orderHistory.dosageCardButtonTooltip')}
            </PharmacTooltip>
            <PharmacButton
              size="small"
              marginMediumRight
              disabled={!dosageCardButtonEnabled}
              variant={!dosageCardButtonEnabled ? 'secondary' : 'primary'}
              onClick={getDosingCardData}
            >
              <PharmacIcon
                size="16px"
                icon="externalLink"
              />
              <PharmacText
                paddingSmallLeft
                paddingSmallRight
              >
                {t('extranet:orderHistory.dosageCards')}
              </PharmacText>
            </PharmacButton>
            <PharmacTooltip
              position="top-end"
              handle={
                <PharmacIcon
                  icon="info"
                  style={{
                    color: 'var(--color-green)',
                    marginRight: 'var(--spacing-tiny)',
                    marginTop: 'var(--spacing-tiny)',
                  }}
                />
              }
            >
              {t('extranet:orderHistory.orderChangesButtonTooltip')}
            </PharmacTooltip>
            <PharmacButton
              paddingSmallRight
              size="small"
              variant={!orderHasChangesStatus ? 'secondary' : 'primary'}
              disabled={!orderHasChangesStatus}
              onClick={getOrderChangesData}
            >
              <PharmacIcon
                size="16px"
                icon="externalLink"
              />

              <PharmacText
                paddingSmallLeft
                paddingSmallRight
              >
                {t('extranet:orderHistory.listOfChangesButton')}
              </PharmacText>
            </PharmacButton>
          </ExtranetContainer>
        )}
      </ExtranetContainer>
      <ExtranetContainer
        full
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetTable
          style={{
            fontFamily: 'var(--font-family-secondary)',
            fontWeight: 300,
          }}
        >
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--extranet-light-purple)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            {minDesktop && (
              <ExtranetTableColumn
                marginSmallLeft
                size={1}
              >
                {t('orderHistory.select')}
              </ExtranetTableColumn>
            )}

            <ExtranetTableColumn size={maxMobile ? 1 : 1.5}>
              {t('orderHistory.orderDate')}
            </ExtranetTableColumn>

            <ExtranetTableColumn
              size={maxMobile ? 1 : 3.5}
              style={{ overflowWrap: 'anywhere' }}
            >
              {t('orderHistory.customerGroup')}
            </ExtranetTableColumn>
            <ExtranetTableColumn
              size={maxMobile ? 1 : 2}
              style={{ overflowWrap: 'anywhere' }}
            >
              {t('orderHistory.groupOrderCount')}
            </ExtranetTableColumn>

            {minDesktop && (
              <ExtranetTableColumn size={2}>{t('orderHistory.orderState')}</ExtranetTableColumn>
            )}
            {!maxMobile && <ExtranetExpandArrowColumn />}
          </ExtranetTableRow>

          <React.Fragment>
            {!noResults &&
              filteredOrders.map((order) => (
                <OrderHistoryCustomerGroup
                  hook={checkboxesHook}
                  key={order.groupId + order.createdAt}
                  order={order}
                />
              ))}
            {noResults && <NoResultsResponse />}
          </React.Fragment>
        </ExtranetTable>
      </ExtranetContainer>
      <div
        ref={scrollRef}
        style={{ height: 200 }}
      />
    </ExtranetWrapper>
  )
}
